import type { SupportedLanguage, SupportedUiLanguage } from "./constants";

export type UiDirection = "ltr" | "rtl";
export type PairSupportMode = "same_language" | "compat_runtime_bridge" | "authored_overlay";

export const RELEASED_UI_LOCALES: ReadonlyArray<{ code: SupportedUiLanguage; direction: UiDirection }> = [
  { code: "en", direction: "ltr" },
  { code: "tr", direction: "ltr" },
  { code: "ar", direction: "rtl" },
];

export const RELEASED_TARGET_LANGUAGES: ReadonlyArray<SupportedLanguage> = [
  "en",
  "tr",
  "ar",
  "it",
];

export const RELEASED_LANGUAGE_PAIRS: ReadonlyArray<{
  uiLanguage: SupportedUiLanguage;
  targetLanguage: SupportedLanguage;
  supportMode: PairSupportMode;
}> = [
  { uiLanguage: "en", targetLanguage: "en", supportMode: "same_language" },
  { uiLanguage: "en", targetLanguage: "tr", supportMode: "compat_runtime_bridge" },
  { uiLanguage: "tr", targetLanguage: "tr", supportMode: "same_language" },
  { uiLanguage: "tr", targetLanguage: "en", supportMode: "compat_runtime_bridge" },
  { uiLanguage: "tr", targetLanguage: "ar", supportMode: "authored_overlay" },
  { uiLanguage: "en", targetLanguage: "ar", supportMode: "authored_overlay" },
  { uiLanguage: "ar", targetLanguage: "ar", supportMode: "same_language" },
  { uiLanguage: "en", targetLanguage: "it", supportMode: "authored_overlay" },
  { uiLanguage: "tr", targetLanguage: "it", supportMode: "authored_overlay" },
  { uiLanguage: "ar", targetLanguage: "en", supportMode: "authored_overlay" },
  { uiLanguage: "ar", targetLanguage: "tr", supportMode: "authored_overlay" },
];

const RELEASED_UI_LOCALE_SET = new Set<string>(RELEASED_UI_LOCALES.map((entry) => entry.code));
const RELEASED_TARGET_LANGUAGE_SET = new Set<string>(RELEASED_TARGET_LANGUAGES);
const UI_DIRECTION_BY_LANGUAGE = new Map<string, UiDirection>(
  RELEASED_UI_LOCALES.map((entry) => [entry.code, entry.direction]),
);
const TARGETS_BY_UI_LANGUAGE = new Map<string, SupportedLanguage[]>();

for (const pair of RELEASED_LANGUAGE_PAIRS) {
  const current = TARGETS_BY_UI_LANGUAGE.get(pair.uiLanguage) || [];
  if (!current.includes(pair.targetLanguage)) {
    current.push(pair.targetLanguage);
  }
  TARGETS_BY_UI_LANGUAGE.set(pair.uiLanguage, current);
}

export function isReleasedUiLanguage(value?: string | null): value is SupportedUiLanguage {
  return RELEASED_UI_LOCALE_SET.has(String(value || "").trim().toLowerCase());
}

export function isReleasedTargetLanguage(value?: string | null): value is SupportedLanguage {
  return RELEASED_TARGET_LANGUAGE_SET.has(String(value || "").trim().toLowerCase());
}

export function isReleasedLanguagePair(uiLanguage?: string | null, targetLanguage?: string | null): boolean {
  const normalizedUiLanguage = String(uiLanguage || "").trim().toLowerCase();
  const normalizedTargetLanguage = String(targetLanguage || "").trim().toLowerCase();
  if (!normalizedUiLanguage || !normalizedTargetLanguage) {
    return false;
  }
  return RELEASED_LANGUAGE_PAIRS.some((pair) => (
    pair.uiLanguage === normalizedUiLanguage
    && pair.targetLanguage === normalizedTargetLanguage
  ));
}

export function getReleasedTargetLanguagesForUi(uiLanguage?: string | null): SupportedLanguage[] {
  const normalizedUiLanguage = String(uiLanguage || "").trim().toLowerCase();
  return [...(TARGETS_BY_UI_LANGUAGE.get(normalizedUiLanguage) || [])];
}

export function getReleasedTargetLanguages(): SupportedLanguage[] {
  return [...RELEASED_TARGET_LANGUAGES];
}

export function getUiLanguageDirection(uiLanguage?: string | null): UiDirection {
  const normalizedUiLanguage = String(uiLanguage || "").trim().toLowerCase();
  return UI_DIRECTION_BY_LANGUAGE.get(normalizedUiLanguage) || "ltr";
}
